import { ChangeEvent, useContext, useState } from 'react';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { Menu, PositionType } from 'common/menu/menu';
import { TrashIcon } from 'assets/icons';
import { Input } from 'common/input';
import { Select } from 'common/select';
import { Toggle } from 'common/toggle';
import { classnames, createDateRange, getDateRange } from 'helpers/utils';
import { appActions, AppContext } from 'context';
import { NotificationObject } from 'models/notificationObject';
import { NotificationType } from 'common/enums';
import styles from './date-menu.module.scss';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const getMonthName = (monthNumber: number): string => {
  if (Number.isNaN(monthNumber) || monthNumber < 1 || monthNumber > 12) {
    return '';
  }
  return MONTHS[monthNumber - 1];
};

interface DateMenuProps {
  currentDate?: string;
  currentFinalDate?: string;
  menuPosition?: PositionType;
  onUpdateDate: (startDate: string, endDate: string) => void;
  renderInline?: boolean;
  // What the date belongs to, for the title and help text.
  subject?: 'album' | 'memory';
}

const SUBJECT_COPY = {
  album: { title: 'Album Date', plural: 'Albums' },
  memory: { title: 'Memory Date', plural: 'Memories' },
};

export const DateMenu = ({
  currentDate,
  currentFinalDate,
  menuPosition = PositionType.BottomRight,
  onUpdateDate,
  renderInline = false,
  subject = 'album',
}: DateMenuProps) => {
  const { startDate, endDate } = getDateRange(
    currentDate,
    currentFinalDate,
    false,
  );
  const [isSpecificDate, setIsSpecificDate] = useState(
    !!startDate &&
      (!endDate || JSON.stringify(startDate) === JSON.stringify(endDate)),
  );
  const [year, setYear] = useState(Number(startDate.year));
  const [month, setMonth] = useState(Number(startDate.month));
  const [day, setDay] = useState(Number(startDate.day));
  const [finalYear, setFinalYear] = useState(Number(endDate.year));
  const [finalMonth, setFinalMonth] = useState(Number(endDate.month));
  const [finalDay, setFinalDay] = useState(Number(endDate.day));
  const { dispatch } = useContext(AppContext);

  const validateYear = (yearToValidate: number) => {
    if (!Number.isNaN(yearToValidate) && yearToValidate <= 0) {
      return 'Year must be a positive number';
    }
    return '';
  };

  const validateDay = (dayToValidate: number) => {
    if (dayToValidate < 1 || dayToValidate > 31) {
      return 'Day must be a number between 1 and 31';
    }
    return '';
  };

  const handleSave = () => {
    if (isSpecificDate) {
      const allEmpty =
        Number.isNaN(year) && Number.isNaN(month) && Number.isNaN(day);
      if (allEmpty) {
        onUpdateDate('', '');
        return;
      }

      const hasError =
        (!Number.isNaN(year) && !!validateYear(year)) ||
        (!Number.isNaN(day) && !!validateDay(day));

      if (hasError) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'The date could not be updated',
            message: 'Please indicate a valid date',
            type: NotificationType.Error,
          }),
        });
        return;
      }

      const { startDate: newStartDate, endDate: newEndDate } = createDateRange(
        { year, month, day },
        { year, month, day },
        true,
      );
      onUpdateDate(newStartDate.toISOString(), newEndDate.toISOString());
    } else {
      const allEmpty =
        Number.isNaN(year) &&
        Number.isNaN(month) &&
        Number.isNaN(day) &&
        Number.isNaN(finalYear) &&
        Number.isNaN(finalMonth) &&
        Number.isNaN(finalDay);

      if (allEmpty) {
        onUpdateDate('', '');
        return;
      }

      const hasError =
        (!Number.isNaN(year) && !!validateYear(year)) ||
        (!Number.isNaN(day) && !!validateDay(day)) ||
        (!Number.isNaN(finalYear) && !!validateYear(finalYear)) ||
        (!Number.isNaN(finalDay) && !!validateDay(finalDay));

      if (hasError) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'The date could not be updated',
            message: 'Please indicate a valid date',
            type: NotificationType.Error,
          }),
        });
        return;
      }

      const { startDate: newStartDate, endDate: newEndDate } = createDateRange(
        { year, month, day },
        { year: finalYear, month: finalMonth, day: finalDay },
        false,
      );

      if (newStartDate > newEndDate) {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'The date could not be updated',
            message: 'The start date must be before the end date',
            type: NotificationType.Error,
          }),
        });
        return;
      }
      onUpdateDate(newStartDate.toISOString(), newEndDate.toISOString());
    }
  };

  const handleRemoveDate = () => {
    if (!currentDate && !currentFinalDate) {
      return;
    }
    onUpdateDate('', '');
  };

  const monthOptions = [
    { id: 0, value: '' },
    ...MONTHS.map((monthName, index) => ({
      id: index + 1,
      value: monthName,
    })),
  ];

  const buildDateInputs = (isFrom: boolean = false) => {
    const currentYear = isFrom ? year : finalYear;
    const currentDay = isFrom ? day : finalDay;
    const currentMonth = isFrom ? month : finalMonth;

    return (
      <div className={styles.inputs}>
        <div className={styles.inputGroup}>
          <Input
            label="Year "
            secondLabel="*"
            id={isFrom ? 'year' : 'final-year'}
            value={Number.isNaN(currentYear) ? '' : currentYear}
            placeholder="YYYY"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              (isFrom ? setYear : setFinalYear)(
                e.target.value === '' ? NaN : Number(e.target.value),
              )
            }
            errorMessage={
              !Number.isNaN(isFrom ? year : finalYear)
                ? validateYear(isFrom ? year : finalYear)
                : ''
            }
            type="number"
          />
        </div>
        <div className={styles.selectContainer}>
          <div className={styles.selectLabel}>
            <span className="text__body__semi__bold__medium__gray800">
              Month
            </span>
            <span className="text__body__regular__medium__gray700">
              {' '}
              (optional)
            </span>
          </div>
          <div className={styles.selectWrapper}>
            <Select
              id={isFrom ? 'month' : 'final-month'}
              options={monthOptions}
              optionSelected={
                !Number.isNaN(currentMonth) && currentMonth > 0
                  ? monthOptions.find((opt) => opt.id === currentMonth) ||
                    monthOptions[0]
                  : monthOptions[0]
              }
              onClickOption={(option) =>
                (isFrom ? setMonth : setFinalMonth)(
                  option.id === 0 ? NaN : (option.id as number),
                )
              }
              value={getMonthName(isFrom ? month : finalMonth)}
              placeholder="Month"
              smallOptions
              inputClassName={styles.dateMenuInput}
              chevronClassName={styles.dateMenuChevron}
            />
          </div>
        </div>
        <div className={styles.inputGroup}>
          <Input
            label="Day "
            secondLabel="(optional)"
            id={isFrom ? 'day' : 'final-day'}
            value={Number.isNaN(currentDay) ? '' : currentDay}
            placeholder="DD"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              (isFrom ? setDay : setFinalDay)(
                e.target.value === '' ? NaN : Number(e.target.value),
              )
            }
            errorMessage={
              !Number.isNaN(isFrom ? day : finalDay)
                ? validateDay(isFrom ? day : finalDay)
                : ''
            }
            type="number"
            min={1}
            max={31}
          />
        </div>
      </div>
    );
  };

  const buildDateInputsWithRange = () => (
    <>
      <div className={styles.dateInputs}>
        <span
          className={classnames(
            'text__body__regular__small__gray800',
            styles.rangeLabel,
          )}
        >
          From:
        </span>
        {buildDateInputs(true)}
      </div>
      <div className={styles.dateInputs}>
        <span
          className={classnames(
            'text__body__regular__small__gray800',
            styles.rangeLabel,
          )}
        >
          To:
        </span>
        {buildDateInputs(false)}
      </div>
    </>
  );

  const body = (
    <>
      <div className={styles.headerContainer}>
        <div className={styles.header}>
          <span className="text__title2__gray900">
            {SUBJECT_COPY[subject].title}
          </span>
          <span className="text__body__regular__medium__gray800">
            Timestamps help organize your {SUBJECT_COPY[subject].plural}. Choose
            a specific date or a range, from just a year to a full date.
          </span>
        </div>

        {isSpecificDate ? buildDateInputs(true) : buildDateInputsWithRange()}

        <div className={styles.toggleContainer}>
          <Toggle
            id="use-range"
            checked={!isSpecificDate}
            onChangeFn={(e) => setIsSpecificDate(!e.target.checked)}
            containerClass={styles.toggleSize}
          />
          <span className="text__body__regular__medium__gray700">
            Use a range
          </span>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          buttonStyle={ButtonStyle.TertiaryGray}
          buttonSize={ButtonSize.Medium}
          onClick={handleRemoveDate}
          className={styles.removeButton}
          disabled={!currentDate && !currentFinalDate}
        >
          <TrashIcon />
          Remove Date
        </Button>
        <Button
          buttonStyle={ButtonStyle.Primary}
          buttonSize={ButtonSize.Medium}
          onClick={handleSave}
          className={styles.saveButton}
        >
          Save Date
        </Button>
      </div>
    </>
  );

  if (renderInline) return body;

  return (
    <Menu
      width={styles.menuWidth}
      position={menuPosition}
      className={styles.menu}
    >
      <div className={styles.container}>{body}</div>
    </Menu>
  );
};
